export const generateUniqueCaseCode = (clid, cases) => {
  const prefix = String(clid || 'CASE');
  const sourceCases = Array.isArray(cases) ? cases : [];
  const usedNumbers = new Set();
  sourceCases.forEach((caseItem) => {
    const caseName = String(caseItem?.name || '');
    if (!caseName.startsWith(`${prefix}.`)) return;
    const suffix = caseName.slice(prefix.length + 1);
    if (!/^\d+$/.test(suffix)) return;
    usedNumbers.add(parseInt(suffix, 10));
  });
  let nextNumber = 1;
  while (usedNumbers.has(nextNumber)) nextNumber += 1;
  return `${prefix}.${String(nextNumber).padStart(4, '0')}`;
};

export const buildPreviewPairsFromMessages = (messages, previousPairs = []) => {
  const sourceMessages = Array.isArray(messages) ? messages : [];
  const sourcePairs = Array.isArray(previousPairs) ? previousPairs : [];
  return sourceMessages.map((message, index) => ({
    client_message: String(message || ''),
    expected_result: String(sourcePairs[index]?.expected_result || ''),
  }));
};

export const normalizeCaseForDraft = (caseItem) => {
  const messages = Array.isArray(caseItem?.messages) && caseItem.messages.length > 0 ? caseItem.messages : [''];
  return {
    ...caseItem,
    annotation: caseItem?.annotation ?? '',
    messages,
    preview_pairs: buildPreviewPairsFromMessages(messages, caseItem?.preview_pairs || []),
  };
};

/**
 * Build new test plan cases from a JSON array of conversations.
 *
 * Each entry may be either:
 *   1. string[] -- a multi-turn conversation (one user message per element, in order).
 *   2. object   -- { messages: string[], name?, annotation?, is_skipped?,
 *      expected_results?: string[] }. `messages` is required.
 */
export const buildCasesFromConversationsJson = (data, clid, existingCases) => {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('JSON must be a non-empty array of conversations.');
  }
  const accumulated = Array.isArray(existingCases) ? [...existingCases] : [];
  const newCases = [];

  const validateMessages = (rawMessages, idx) => {
    if (!Array.isArray(rawMessages) || rawMessages.length === 0) {
      throw new Error(`Conversation #${idx + 1} must have a non-empty messages array.`);
    }
    return rawMessages.map((message, msgIdx) => {
      if (typeof message !== 'string' || !message.trim()) {
        throw new Error(`Conversation #${idx + 1} message #${msgIdx + 1} must be a non-empty string.`);
      }
      return message;
    });
  };

  data.forEach((entry, idx) => {
    let messages;
    let name;
    let annotation = '';
    let isSkipped = false;
    let previewPairs = [];

    if (Array.isArray(entry)) {
      messages = validateMessages(entry, idx);
    } else if (entry && typeof entry === 'object') {
      messages = validateMessages(entry.messages, idx);
      if (entry.name != null && String(entry.name).trim()) {
        name = String(entry.name).trim();
      }
      annotation = entry.annotation != null ? String(entry.annotation) : '';
      isSkipped = !!entry.is_skipped;
      if (Array.isArray(entry.expected_results)) {
        previewPairs = messages.map((message, msgIdx) => ({
          client_message: message,
          expected_result: String(entry.expected_results[msgIdx] ?? ''),
        }));
      } else if (Array.isArray(entry.preview_pairs)) {
        previewPairs = entry.preview_pairs;
      }
    } else {
      throw new Error(`Conversation #${idx + 1} must be an array of strings or an object with a messages array.`);
    }

    const builtCase = normalizeCaseForDraft({
      name: name || generateUniqueCaseCode(clid, accumulated),
      messages,
      is_skipped: isSkipped,
      annotation,
      preview_pairs: previewPairs,
    });
    accumulated.push(builtCase);
    newCases.push(builtCase);
  });
  return newCases;
};

/**
 * Serialize draft cases to the same JSON array shape accepted by
 * buildCasesFromConversationsJson (round-trip for export / edit / apply).
 */
export const serializeTestPlanCasesToImportJson = (cases) => {
  const sourceCases = Array.isArray(cases) ? cases : [];
  return sourceCases.map((caseItem) => {
    const messages = Array.isArray(caseItem?.messages) ? caseItem.messages.map((m) => String(m ?? '')) : [];
    const pairs = Array.isArray(caseItem?.preview_pairs) ? caseItem.preview_pairs : [];
    const entry = {
      name: String(caseItem?.name || '').trim(),
      annotation: caseItem?.annotation != null ? String(caseItem.annotation) : '',
      is_skipped: !!caseItem?.is_skipped,
      messages,
      expected_results: messages.map((_, msgIdx) => String(pairs[msgIdx]?.expected_result ?? '')),
    };
    return entry;
  });
};

const parseCsvMatrix = (text) => {
  const raw = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!raw) {
    throw new Error('CSV file is empty.');
  }
  const lines = [];
  let i = 0;
  while (i < raw.length) {
    const row = [];
    while (i < raw.length && raw[i] !== '\n' && raw[i] !== '\r') {
      if (raw[i] === '"') {
        i += 1;
        let cell = '';
        while (i < raw.length) {
          if (raw[i] === '"') {
            i += 1;
            if (raw[i] === '"') {
              cell += '"';
              i += 1;
            } else break;
          } else {
            cell += raw[i];
            i += 1;
          }
        }
        row.push(cell);
        if (raw[i] === ',') i += 1;
      } else {
        let cell = '';
        while (i < raw.length && raw[i] !== ',' && raw[i] !== '\n' && raw[i] !== '\r') {
          cell += raw[i];
          i += 1;
        }
        row.push(cell.replace(/^"|"$/g, '').replace(/""/g, '"'));
        if (raw[i] === ',') i += 1;
      }
    }
    if (row.some((cell) => String(cell || '').trim() !== '')) {
      lines.push(row);
    }
    if (raw[i] === '\r') i += 1;
    if (raw[i] === '\n') i += 1;
  }
  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row.');
  }
  return lines;
};

const buildCsvHeaderIndex = (headerRow) => {
  const headerMap = {};
  headerRow.forEach((header, idx) => {
    const normalized = String(header || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!normalized) return;
    headerMap[normalized] = idx;
    const compact = normalized.replace(/_/g, '');
    if (headerMap[compact] == null) headerMap[compact] = idx;
  });
  return headerMap;
};

const getCsvCell = (row, headerMap, ...names) => {
  for (const name of names) {
    const key = String(name || '').trim().toLowerCase().replace(/\s+/g, '_');
    const idx = headerMap[key] ?? headerMap[key.replace(/_/g, '')];
    if (idx != null && row[idx] != null) {
      return String(row[idx]).trim();
    }
  }
  return '';
};

/**
 * Build test plan cases from a Conversations-page CSV export.
 *
 * Expected header: conversation, user message, ai message
 * Rows with the same conversation id are grouped into one multi-turn case.
 * Different conversation ids become separate cases.
 */
export const buildCasesFromConversationExportCsv = (text, clid, existingCases) => {
  const matrix = parseCsvMatrix(text);
  const headerMap = buildCsvHeaderIndex(matrix[0]);
  const conversationIdx = headerMap.conversation ?? headerMap.conversation_id;
  const userIdx = headerMap.user_message ?? headerMap.usermessage;
  const aiIdx = headerMap.ai_message ?? headerMap.aimessage;
  if (conversationIdx == null || userIdx == null || aiIdx == null) {
    throw new Error('CSV header must include conversation, user message, and ai message columns.');
  }

  const grouped = new Map();
  const groupOrder = [];

  for (let rowIdx = 1; rowIdx < matrix.length; rowIdx += 1) {
    const row = matrix[rowIdx];
    const conversationId = getCsvCell(row, headerMap, 'conversation', 'conversation_id') || `row-${rowIdx}`;
    const userMessage = getCsvCell(row, headerMap, 'user_message', 'user message');
    const aiMessage = getCsvCell(row, headerMap, 'ai_message', 'ai message');
    if (!userMessage) {
      throw new Error(`CSV row #${rowIdx + 1} must have a non-empty user message.`);
    }
    if (!grouped.has(conversationId)) {
      grouped.set(conversationId, { messages: [], expectedResults: [] });
      groupOrder.push(conversationId);
    }
    const group = grouped.get(conversationId);
    group.messages.push(userMessage);
    group.expectedResults.push(aiMessage);
  }

  if (groupOrder.length === 0) {
    throw new Error('CSV has no valid data rows.');
  }

  const accumulated = Array.isArray(existingCases) ? [...existingCases] : [];
  const newCases = [];

  groupOrder.forEach((conversationId) => {
    const group = grouped.get(conversationId);
    const builtCase = normalizeCaseForDraft({
      name: generateUniqueCaseCode(clid, accumulated),
      messages: group.messages,
      is_skipped: false,
      annotation: conversationId.startsWith('row-') ? '' : conversationId,
      preview_pairs: group.messages.map((message, msgIdx) => ({
        client_message: message,
        expected_result: String(group.expectedResults[msgIdx] ?? ''),
      })),
    });
    accumulated.push(builtCase);
    newCases.push(builtCase);
  });

  return newCases;
};

const looksLikeJsonImport = (text) => {
  const trimmed = String(text || '').trim();
  return trimmed.startsWith('[') || trimmed.startsWith('{');
};

/**
 * Parse an import file body as JSON or conversation-export CSV.
 */
export const buildTestPlanCasesFromImportText = (text, filename, clid, existingCases) => {
  const lowerName = String(filename || '').toLowerCase();
  const preferCsv = lowerName.endsWith('.csv');
  const preferJson = lowerName.endsWith('.json');

  if (preferCsv && !preferJson) {
    return buildCasesFromConversationExportCsv(text, clid, existingCases);
  }
  if (preferJson) {
    const parsed = JSON.parse(text || 'null');
    return buildCasesFromConversationsJson(parsed, clid, existingCases);
  }
  if (looksLikeJsonImport(text)) {
    const parsed = JSON.parse(text || 'null');
    return buildCasesFromConversationsJson(parsed, clid, existingCases);
  }
  return buildCasesFromConversationExportCsv(text, clid, existingCases);
};
